// adminportal/src/pages/UpdateUser.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PasswordResetModal from '../components/PasswordResetModal';
import Copyright from '../components/Copyright';

interface UserData {
  userId: string; // required for PUT/DELETE
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string; // API returns "role", not "roleId"
  companyId: string;
  companyName?: string;
  companyUrl?: string;
  companyState?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  mfaRequired?: boolean;
  isServiceAccount?: boolean;
}

import { apiUrl, isApiUrlConfigured } from '../config';

const UpdateUser: React.FC = () => {
  const [allUsers, setAllUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    removeFolders: boolean;
  }>({ show: false, removeFolders: false });
  const [passwordModal, setPasswordModal] = useState<{
    show: boolean;
  }>({ show: false });

  // Load all users on component mount
  useEffect(() => {
    loadAllUsers();
  }, []);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(allUsers);
    } else {
      const filtered = allUsers.filter(
        user =>
          (user.firstName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
          (user.lastName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
          (user.email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
          (user.username?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
          (user.companyName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
          (user.role?.toLowerCase() || '').includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, allUsers]);

  const loadAllUsers = async () => {
    setLoading(true);
    setMessage('');
    try {
      if (!isApiUrlConfigured()) {
        console.warn('⚠️ API URL not configured');
        setLoading(false);
        return;
      }
      const url = apiUrl('users');
      if (!url) {
        console.warn('⚠️ API URL not configured');
        setLoading(false);
        return;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      if (data.success && data.users) {
        // Filter out service accounts for the update user page
        const regularUsers = data.users.filter((user: UserData) => !user.isServiceAccount);
        setAllUsers(regularUsers);
        setFilteredUsers(regularUsers);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to load users: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
      setAllUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleUserSelect = (user: UserData) => {
    setUserData(user);
    setSearchQuery(`${user.firstName} ${user.lastName} (${user.email})`);
  };

  const handleCancel = () => {
    setUserData(null);
    setSearchQuery('');
  };

  const handleUpdate = async () => {
    if (!userData) return;
    setLoading(true);
    setMessage('');
    try {
      const { companyId, userId, ...updates } = userData;

      const res = await fetch(`${apiUrl('users')}/${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, ...updates }),
      });

      if (!res.ok) throw new Error(await res.text());
      setMessage('✅ User updated successfully.');

      // Redirect to dashboard after successful update
      setTimeout(() => {
        window.location.href = '/admin-dashboard';
      }, 1500);
    } catch (err: unknown) {
      setMessage(`❌ Failed to update user. ${err instanceof Error ? `(${err.message})` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteModal({ show: true, removeFolders: false });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, removeFolders: false });
  };

  const openPasswordModal = () => {
    setPasswordModal({ show: true });
  };

  const closePasswordModal = () => {
    setPasswordModal({ show: false });
  };

  const handlePasswordResetSuccess = (message: string) => {
    setMessage(message);
  };

  const handlePasswordResetError = (message: string) => {
    setMessage(message);
  };

  const handleDelete = async () => {
    if (!userData) return;

    setLoading(true);
    setMessage('');
    try {
      // Delete user with folder cleanup option
      const res = await fetch(
        `${apiUrl('users')}/${encodeURIComponent(userData.userId)}?companyId=${encodeURIComponent(userData.companyId)}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ removeFolders: deleteModal.removeFolders }),
        }
      );

      if (!res.ok) throw new Error(await res.text());

      setUserData(null);
      setSearchQuery('');
      setMessage(
        `✅ User deleted successfully${deleteModal.removeFolders ? ' with folders removed' : ''}`
      );
      closeDeleteModal();
    } catch (err: unknown) {
      setMessage(`❌ Failed to delete user. ${err instanceof Error ? `(${err.message})` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='p-4 max-w-lg mx-auto'>
      <div className='flex items-center justify-between mb-4'>
        <h1 className='text-xl font-semibold text-slate-100'>Update User</h1>
        <Link to='/admin-dashboard' className='text-blue-400 hover:text-blue-300 text-sm'>
          ← Back to Dashboard
        </Link>
      </div>

      <div className='mb-6'>
        <label className='block text-sm font-medium mb-2 text-slate-200'>Filter Users</label>
        <input
          type='text'
          value={searchQuery}
          onChange={e => handleSearchChange(e.target.value)}
          className='w-full px-3 py-2 border border-white/10 bg-white/5 text-slate-100 placeholder:text-slate-400 rounded outline-none focus-visible:ring-2 focus-visible:ring-indigo-400'
          placeholder='Filter by name, email, username, company, or role...'
        />
        <p className='text-xs text-slate-400 mt-1'>Type to filter users by any field</p>
      </div>

      {/* Users List */}
      <div className='mb-6'>
        <div className='flex items-center justify-between mb-3'>
          <h2 className='text-lg font-medium text-slate-200'>Select User to Update</h2>
          <span className='text-sm text-slate-400'>
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className='text-center py-8 text-slate-400'>Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className='text-center py-8 text-slate-400'>
            {searchQuery ? 'No users match your filter' : 'No users found'}
          </div>
        ) : (
          <div className='bg-slate-800/50 border border-white/10 rounded-lg max-h-96 overflow-y-auto'>
            <div className='divide-y divide-white/5'>
              {filteredUsers.map(user => (
                <button
                  key={user.userId}
                  onClick={() => handleUserSelect(user)}
                  className={`w-full px-4 py-3 text-left hover:bg-slate-700/50 transition-colors ${
                    userData?.userId === user.userId
                      ? 'bg-indigo-900/30 border-l-4 border-indigo-400'
                      : ''
                  }`}
                >
                  <div className='flex items-start justify-between'>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center space-x-2 mb-1'>
                        <span className='font-medium text-slate-100 truncate'>
                          {user.firstName || 'Unknown'} {user.lastName || 'User'}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            user.role === 'admin'
                              ? 'bg-purple-500/20 text-purple-400'
                              : user.role === 'super-user'
                                ? 'bg-orange-500/20 text-orange-400'
                                : user.role === 'standard-user'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-slate-500/20 text-slate-400'
                          }`}
                        >
                          {(user.role || 'unknown').replace('-', ' ')}
                        </span>
                        {user.status && (
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${
                              user.status === 'active'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : user.status === 'inactive'
                                  ? 'bg-slate-500/20 text-slate-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                            }`}
                          >
                            {user.status}
                          </span>
                        )}
                      </div>
                      <div className='text-sm text-slate-400 mb-1'>{user.email || 'No email'}</div>
                      <div className='text-xs text-slate-500'>
                        {user.companyName && <span className='mr-3'>🏢 {user.companyName}</span>}
                        <span>👤 {user.username || 'No username'}</span>
                      </div>
                      {user.createdAt && (
                        <div className='text-xs text-slate-500 mt-1'>
                          Created: {new Date(user.createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    {userData?.userId === user.userId && (
                      <div className='ml-2 text-indigo-400'>✓ Selected</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {userData && (
        <>
          <div className='mb-4 p-4 bg-green-900/20 border border-green-700/50 rounded-lg'>
            <p className='text-green-300 text-sm mb-2'>✅ User selected successfully!</p>
            <p className='text-slate-300 text-xs'>You can now update the user information below.</p>
          </div>

          <div className='mb-4'>
            <label htmlFor='firstName' className='block text-sm font-medium text-slate-200'>
              First Name
            </label>
            <input
              id='firstName'
              type='text'
              className='w-full px-3 py-2 border border-white/10 bg-white/5 text-slate-100 placeholder:text-slate-400 rounded outline-none focus-visible:ring-2 focus-visible:ring-indigo-400'
              value={userData.firstName || ''}
              onChange={e => setUserData({ ...userData, firstName: e.target.value })}
            />
          </div>

          <div className='mb-4'>
            <label htmlFor='lastName' className='block text-sm font-medium text-slate-200'>
              Last Name
            </label>
            <input
              id='lastName'
              type='text'
              className='w-full px-3 py-2 border border-white/10 bg-white/5 text-slate-100 placeholder:text-slate-400 rounded outline-none focus-visible:ring-2 focus-visible:ring-indigo-400'
              value={userData.lastName || ''}
              onChange={e => setUserData({ ...userData, lastName: e.target.value })}
            />
          </div>

          <div className='mb-4'>
            <label htmlFor='email' className='block text-sm font-medium text-slate-200'>
              Email
            </label>
            <input
              id='email'
              type='email'
              className='w-full px-3 py-2 border border-white/10 bg-white/5 text-slate-100 placeholder:text-slate-400 rounded outline-none focus-visible:ring-2 focus-visible:ring-indigo-400'
              value={userData.email || ''}
              onChange={e => setUserData({ ...userData, email: e.target.value })}
            />
          </div>

          <div className='mb-4'>
            <label className='block text-sm font-medium text-slate-200'>Company</label>
            <div className='p-3 bg-white/5 border border-white/10 rounded'>
              <div className='text-sm font-medium text-slate-100'>
                {userData.companyName || 'No company'}
              </div>
              {userData.companyState && userData.companyUrl && (
                <div className='text-xs text-slate-400'>
                  {userData.companyState} • {userData.companyUrl}
                </div>
              )}
            </div>
          </div>

          <div className='mb-4'>
            <label htmlFor='userRole' className='block text-sm font-medium text-slate-200'>
              User Role
            </label>
            <select
              id='userRole'
              value={userData.role || 'standard-user'}
              onChange={e => setUserData({ ...userData, role: e.target.value })}
              className='w-full px-3 py-2 border rounded'
            >
              <option value='standard-user'>
                Standard User - Company shared folders (read) + own folders (full)
              </option>
              <option value='super-user'>
                Super User - Company folders (full) + own folders (full)
              </option>
              <option value='admin'>Admin - All companies and all users (full access)</option>
            </select>

            <p className='text-sm text-slate-400 mt-1'>
              {userData.role === 'standard-user' &&
                'Access to company inbound/outbound (read-only) + personal folders (full access)'}
              {userData.role === 'super-user' &&
                'Access to all company folders + personal folders (full access)'}
              {userData.role === 'admin' &&
                'Access to everything across all companies (full access)'}
            </p>
          </div>

          <div className='mb-4'>
            <label className='block text-sm font-medium text-slate-200'>
              Multi-Factor Authentication (MFA)
            </label>

            <div className='space-y-2'>
              <label className='flex items-center'>
                <input
                  type='checkbox'
                  checked={userData.mfaRequired || false}
                  onChange={e => setUserData({ ...userData, mfaRequired: e.target.checked })}
                  className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 rounded focus:ring-blue-500 focus:ring-2'
                />
                <span className='text-sm text-slate-200'>Require MFA for this user</span>
              </label>

              {(userData.role === 'admin' || userData.role === 'super-user') && (
                <div className='p-3 bg-amber-500/20 border border-amber-500/30 rounded'>
                  <p className='text-xs text-amber-200'>
                    🔒 <strong>Security Recommendation:</strong> MFA is strongly recommended for{' '}
                    {userData.role === 'admin' ? 'admin' : 'super'} users due to their elevated
                    system access. This helps protect sensitive company data and system
                    configurations.
                  </p>
                </div>
              )}

              <p className='text-xs text-slate-400'>
                MFA adds an extra layer of security by requiring a second form of authentication
                (e.g., SMS, authenticator app). Recommended for all users, especially those with
                elevated permissions.
              </p>
            </div>
          </div>

          <div className='flex space-x-4'>
            <button
              type='button'
              onClick={handleCancel}
              className='flex-1 px-4 py-2 border border-white/10 rounded text-slate-200 hover:bg-white/10 transition-colors'
            >
              Cancel
            </button>
            <button
              onClick={openPasswordModal}
              disabled={loading}
              className='bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors'
            >
              Reset Password
            </button>
            <button
              onClick={handleUpdate}
              disabled={loading}
              className='bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 transition-colors'
            >
              {loading ? 'Saving...' : 'Update User'}
            </button>
            <button
              onClick={openDeleteModal}
              disabled={loading}
              className='bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 transition-colors'
            >
              Delete User
            </button>
          </div>
        </>
      )}

      {message && <p className='mt-4 text-sm text-slate-300'>{message}</p>}

      {deleteModal.show && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-slate-800 p-6 rounded-lg shadow-xl max-w-md w-full'>
            <h3 className='text-xl font-semibold text-slate-100 mb-4'>Confirm Deletion</h3>
            <p className='text-slate-300 mb-4'>
              Are you sure you want to delete user {userData?.email}? This action cannot be undone.
            </p>

            <div className='mb-4'>
              <label className='flex items-center gap-2 text-sm text-slate-300'>
                <input
                  type='checkbox'
                  checked={deleteModal.removeFolders}
                  onChange={e =>
                    setDeleteModal(prev => ({ ...prev, removeFolders: e.target.checked }))
                  }
                  className='rounded border-white/10'
                />
                Remove S3 folders and files
              </label>
              <p className='text-xs text-slate-400 mt-1'>
                Check this to permanently delete all user data from S3
              </p>
            </div>

            <div className='flex justify-end space-x-2'>
              <button
                onClick={closeDeleteModal}
                className='px-4 py-2 border border-white/10 rounded text-slate-200 hover:bg-white/10 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className='bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 transition-colors'
              >
                {loading ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={passwordModal.show}
        onClose={closePasswordModal}
        userId={userData?.userId || ''}
        userEmail={userData?.email || ''}
        onSuccess={handlePasswordResetSuccess}
        onError={handlePasswordResetError}
      />

      {/* Copyright with Easter egg */}
      <Copyright className='mt-8' showEasterEgg={true} />
    </div>
  );
};

export default UpdateUser;

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
